/**
 * DAG progress strip rendered above the sub-agent card it belongs to.
 *
 * Receives a dag_progress Turn (the row's ContentJson.stages) and renders
 * a horizontal sequence of stage nodes with status-driven visual treatment
 * per CHAT_DAG_PROGRESS_v0_spec.md §6.
 *
 * Stage list is fully data-driven — the backend authoritatively writes
 * keys and labels into ContentJson at row insert; the frontend never
 * invents them. Spec §3.1 §6 ("What the frontend does NOT do").
 */
import React from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { DagProgressStatus, DagStage } from './types';

interface DagProgressProps {
  stages: DagStage[];
}

const STATUS_ICON_SIZE = 14;

const StageNode: React.FC<{ stage: DagStage }> = ({ stage }) => {
  const { status, label } = stage;
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{
        opacity: status === 'pending' ? 0.45 : 1,
      }}
    >
      <StatusGlyph status={status} />
      <Typography
        variant="caption"
        sx={{
          color:
            status === 'failed'
              ? 'error.main'
              : status === 'pending'
                ? 'text.secondary'
                : 'text.primary',
          fontWeight: status === 'started' ? 600 : 400,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
};

const StatusGlyph: React.FC<{ status: DagProgressStatus }> = ({ status }) => {
  if (status === 'started') {
    return (
      <CircularProgress
        size={STATUS_ICON_SIZE}
        thickness={5}
        sx={{ color: 'primary.main' }}
      />
    );
  }
  if (status === 'completed') {
    return (
      <CheckCircleIcon
        sx={{ fontSize: STATUS_ICON_SIZE, color: 'success.main' }}
      />
    );
  }
  if (status === 'failed') {
    return (
      <ErrorIcon sx={{ fontSize: STATUS_ICON_SIZE, color: 'error.main' }} />
    );
  }
  // pending: reserve the icon slot so labels line up across statuses.
  return <Box sx={{ width: STATUS_ICON_SIZE, height: STATUS_ICON_SIZE }} />;
};

export const DagProgress: React.FC<DagProgressProps> = ({ stages }) => {
  if (stages.length === 0) return null;
  // Once every stage has reached a terminal status the DAG is done — the
  // sub-agent has finished its planning phase and either a param card or
  // result card has emitted below. Grey the strip so the user reads it as
  // history, not as something to track. Any 'started'/'pending' keeps the
  // strip live.
  const isComplete = stages.every(
    (s) => s.status === 'completed' || s.status === 'failed'
  );
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        py: 0.25,
        opacity: isComplete ? 0.5 : 1,
        transition: 'opacity 150ms ease',
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          px: 1.25,
          py: 0.5,
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        {stages.map((stage, i) => (
          <React.Fragment key={stage.key}>
            <StageNode stage={stage} />
            {i < stages.length - 1 && (
              <ChevronRightIcon
                sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
              />
            )}
          </React.Fragment>
        ))}
      </Stack>
    </Box>
  );
};
